import type { ASTNode } from "./parser";

// Built-in math functions — resolved by name during CallExpression evaluation.
// This is the complete allowed set; any other function name throws at evaluation time.
const MATH_FUNCTIONS: Record<string, (...args: number[]) => number> = {
  abs: Math.abs,
  sqrt: Math.sqrt,
  pow: Math.pow,
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  log: Math.log,
  exp: Math.exp,
  min: Math.min,
  max: Math.max,
  round: Math.round,
  floor: Math.floor,
  ceil: Math.ceil,
  rad: (x: number) => x * (Math.PI / 180),
  deg: (x: number) => x * (180 / Math.PI),
};

// Built-in math constants — resolved before user variables so they cannot be shadowed.
const MATH_CONSTANTS: Record<string, number> = {
  PI: Math.PI,
  E: Math.E,
};

/**
 * Recursively evaluate an AST node with the given variable bindings.
 * Returns a number. Non-finite results (NaN, Infinity, -Infinity) are valid
 * return values and must be handled by the caller.
 */
export function evaluate(
  node: ASTNode,
  variables: Record<string, number>,
): number {
  switch (node.type) {
    case "Literal":
      return node.value;

    case "Identifier":
      if (node.name in MATH_CONSTANTS) {
        return MATH_CONSTANTS[node.name];
      }
      if (node.name in variables) {
        return variables[node.name];
      }
      throw new Error(`Undefined variable: '${node.name}'`);

    case "BinaryExpression": {
      const left = evaluate(node.left, variables);
      const right = evaluate(node.right, variables);
      switch (node.operator) {
        case "+":
          return left + right;
        case "-":
          return left - right;
        case "*":
          return left * right;
        case "/":
          return left / right;
        case "%":
          return left % right;
        case "^":
          return Math.pow(left, right);
        default:
          throw new Error(`Unknown operator: '${node.operator}'`);
      }
    }

    case "UnaryExpression": {
      const operator: string = node.operator;
      switch (operator) {
        case "-":
          return -evaluate(node.argument, variables);
        default:
          throw new Error(`Unknown unary operator: '${operator}'`);
      }
    }

    case "CallExpression": {
      if (!(node.callee in MATH_FUNCTIONS)) {
        throw new Error(`Undefined function: '${node.callee}'`);
      }
      const args = node.arguments.map((arg) => evaluate(arg, variables));
      return MATH_FUNCTIONS[node.callee](...args);
    }
  }
}
